import { Component, Element, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-roi-benefits-type-1';

/**
 * ROI benefits banner emphasising headline performance numbers.
 * Header (heading + subtitle) above a blue background card containing large stat items in a single row separated by vertical dividers.
 * Single-line big-number banner (value + label only); pick type-2 for captioned column stats or type-3 for a 2×2 card grid.
 *
 * @category SignNow
 * @slot heading - <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]>
 * @slot subtitle - <me-text variant="large">
 * @example
 *   <sn-roi-benefits-type-1
 *     stats='[{"value":"28M+","label":"Documents signed"},{"value":"13+","label":"Years in business"}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">ROI at a Glance</me-heading>
 *     <me-text slot="subtitle" variant="large">Key performance indicators.</me-text>
 *   </sn-roi-benefits-type-1>
 */
@Component({
  tag: 'sn-roi-benefits-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcRoiSimple {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  @Element() el: HTMLElement;

  /**
   * Stat items displayed in a row with dividers.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} value - Large numeric or text value
   * @item.property {string} label - Label below the value
   * @item.required value
   * @item.required label
   * @item.content value label
   * @minItems 3
   */
  @Prop({ mutable: true }) stats?: { value?: string; label?: string }[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `stats` prop.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} subtitle - Fallback subtitle (use slot="subtitle" instead)
   * @property {array} stats - Fallback stat items (use stats prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        subtitle?: string;
        raw_html?: string;
        stats?: { value?: string; label?: string }[];
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.stats = parseJsonProp(this.stats);
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const stats = Array.isArray(this.stats)
      ? this.stats
      : (Array.isArray(values?.stats) ? values.stats : []);

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <div class={`${COMPONENT_NAME}__header`}>
          <slot name="heading" />
          <slot name="subtitle" />
        </div>
        {stats.length > 0 && (
          <div class={`${COMPONENT_NAME}__card`}>
            <div class={`${COMPONENT_NAME}__stats`}>
              {stats.map(stat => (
                <div class={`${COMPONENT_NAME}__stat`}>
                  {stat.value && <span class={`${COMPONENT_NAME}__value`}>{stat.value}</span>}
                  {stat.label && <span class={`${COMPONENT_NAME}__label`}>{stat.label}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </me-section>
    );
  }
}
